'use client'

import React from 'react'

import { cn } from '@/utilities/cn'

export type Clip = { url: string; poster?: string; caption?: string }

/**
 * Лента вертикальных роликов — история выездов.
 *
 * На большом экране карточки сами проигрываются без звука, пока видны:
 * страница оживает, а трафик тратится только на то, что человек смотрит.
 * На телефоне автозапуска нет — там показываем обложку, чтобы не жечь
 * мобильный интернет. Клик открывает ролик во весь экран со звуком.
 */
export function VideoReel({ clips, light }: { clips: Clip[]; light?: boolean }) {
  const [open, setOpen] = React.useState<number | null>(null)
  const [auto, setAuto] = React.useState(false)
  const row = React.useRef<HTMLDivElement>(null)
  const cards = React.useRef<(HTMLVideoElement | null)[]>([])

  const close = React.useCallback(() => setOpen(null), [])
  const step = React.useCallback(
    (d: number) => setOpen((i) => (i === null ? null : (i + d + clips.length) % clips.length)),
    [clips.length],
  )

  // мышь есть и экран широкий — можно показывать карточки «живыми»
  React.useEffect(() => {
    const mq = window.matchMedia('(min-width: 640px) and (hover: hover)')
    const apply = () => setAuto(mq.matches && !window.matchMedia('(prefers-reduced-motion: reduce)').matches)
    apply()
    mq.addEventListener('change', apply)
    return () => mq.removeEventListener('change', apply)
  }, [])

  // играет только то, что видно на экране
  React.useEffect(() => {
    if (!auto) return
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          const v = e.target as HTMLVideoElement
          if (e.isIntersecting) void v.play().catch(() => {})
          else v.pause()
        }
      },
      { threshold: 0.5 },
    )
    for (const v of cards.current) if (v) io.observe(v)
    return () => io.disconnect()
  }, [auto, clips.length])

  React.useEffect(() => {
    if (open === null) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
      if (e.key === 'ArrowRight') step(1)
      if (e.key === 'ArrowLeft') step(-1)
    }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [open, close, step])

  const scrollBy = (dir: number) => {
    const el = row.current
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: 'smooth' })
  }

  return (
    <>
      <div className="group relative">
        <div className="no-scrollbar flex snap-x gap-4 overflow-x-auto pb-1" ref={row}>
          {clips.map((c, i) => (
            <figure className="flex w-48 shrink-0 snap-start flex-col gap-2 sm:w-56 lg:w-64" key={c.url}>
              <button
                aria-label={c.caption ? `Смотреть: ${c.caption}` : `Смотреть ролик ${i + 1}`}
                className="relative aspect-[9/16] w-full cursor-pointer overflow-hidden rounded-2xl bg-ink/40"
                onClick={() => setOpen(i)}
                type="button"
              >
                <video
                  className="h-full w-full object-cover"
                  loop
                  muted
                  playsInline
                  poster={c.poster}
                  preload={auto ? 'metadata' : 'none'}
                  ref={(el) => {
                    cards.current[i] = el
                  }}
                  src={auto ? c.url : undefined}
                />
                <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <span className="flex size-12 items-center justify-center rounded-full bg-ink/55 pl-1 text-xl text-sand backdrop-blur-sm transition-transform group-hover:scale-100">
                    ▶
                  </span>
                </span>
              </button>
              {c.caption && (
                <figcaption className={cn('text-sm leading-snug', light ? 'text-sand/75' : 'text-ink/70')}>{c.caption}</figcaption>
              )}
            </figure>
          ))}
        </div>

        {clips.length > 2 && (
          <>
            <button
              aria-label="Прокрутить влево"
              className="absolute top-[38%] left-2 hidden -translate-y-1/2 rounded-full bg-ink/55 px-3.5 py-2.5 font-display text-lg text-sand hover:bg-ink/80 sm:block"
              onClick={() => scrollBy(-1)}
              type="button"
            >
              ‹
            </button>
            <button
              aria-label="Прокрутить вправо"
              className="absolute top-[38%] right-2 hidden -translate-y-1/2 rounded-full bg-ink/55 px-3.5 py-2.5 font-display text-lg text-sand hover:bg-ink/80 sm:block"
              onClick={() => scrollBy(1)}
              type="button"
            >
              ›
            </button>
          </>
        )}
      </div>

      {open !== null && clips[open] && (
        <div
          aria-modal
          className="fixed inset-0 z-[100] flex flex-col bg-ink/95 backdrop-blur-sm"
          onClick={close}
          role="dialog"
        >
          <div className="flex items-center justify-between gap-4 px-4 py-3 text-sand">
            <span className="min-w-0 truncate text-sm">{clips[open]!.caption ?? `${open + 1} / ${clips.length}`}</span>
            <button
              aria-label="Закрыть"
              className="shrink-0 rounded-full border border-sand/30 px-4 py-1.5 font-display text-sm uppercase tracking-wide hover:bg-sand/10"
              onClick={close}
              type="button"
            >
              Закрыть
            </button>
          </div>

          <div className="flex min-h-0 flex-1 items-center justify-center px-2 pb-4">
            <video
              autoPlay
              className="max-h-full max-w-full rounded-xl"
              controls
              key={clips[open]!.url}
              onClick={(e) => e.stopPropagation()}
              playsInline
              poster={clips[open]!.poster}
              src={clips[open]!.url}
            />
          </div>

          {clips.length > 1 && (
            <>
              <button
                aria-label="Предыдущий ролик"
                className="absolute top-1/2 left-2 -translate-y-1/2 rounded-full bg-ink/60 px-4 py-3 font-display text-xl text-sand hover:bg-ink/80"
                onClick={(e) => {
                  e.stopPropagation()
                  step(-1)
                }}
                type="button"
              >
                ‹
              </button>
              <button
                aria-label="Следующий ролик"
                className="absolute top-1/2 right-2 -translate-y-1/2 rounded-full bg-ink/60 px-4 py-3 font-display text-xl text-sand hover:bg-ink/80"
                onClick={(e) => {
                  e.stopPropagation()
                  step(1)
                }}
                type="button"
              >
                ›
              </button>
            </>
          )}
        </div>
      )}
    </>
  )
}
